/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Mail, MessageSquare, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import './Assessment.css';

const API = process.env.REACT_APP_BACKEND_URL;

/* ================================================================
   REPORT PAGE — Section 4.2, Brand v2.1
   One-page report: heading, lead, recommended card, other option,
   remaining line, personal message, footer + buttons.
   ================================================================ */

export default function AssessmentResults() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailSent, setEmailSent] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [emailAddr, setEmailAddr] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/free-assessment/${attemptId}/results`);
        setData(res.data);
        setEmailSent(!!res.data.pathway?.report_email_sent);
        setFeedbackDone(!!res.data.pathway?.feedback_completed);
        const q1 = res.data.answers?.Q1;
        const email = (typeof q1 === 'object' ? q1?.email : '') || res.data.email || '';
        setEmailAddr(email);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [attemptId]);

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await axios.post(`${API}/api/free-assessment/${attemptId}/email-report`, { email: emailAddr });
      setEmailSent(true);
      setEmailConfirm(`Your report is on its way to ${emailAddr}.`);
      setShowEmailPopup(false);
      toast.success(`Report sent to ${emailAddr}`);
    } catch (err) {
      toast.error('Could not send report email.');
    }
    setSendingEmail(false);
  };

  if (loading) return (
    <div className="rpt-loading" data-testid="report-loading">
      <div className="rpt-loading-inner">
        <div className="assess-spinner" />
        <h2>Loading your report...</h2>
      </div>
    </div>
  );

  if (!data) return (
    <div className="rpt-loading"><div className="rpt-loading-inner"><h2>Report not found</h2></div></div>
  );

  const pw = data.pathway || {};
  const report = pw.report || {};
  const written = pw.written || {};
  const answers = data.answers || {};
  const q1 = answers.Q1 || {};
  const preferredName = (typeof q1 === 'object' ? q1.preferred_name : q1) || 'there';
  const isExplore = pw.result_type === 'explore_all_three';
  const exploreFacts = pw.explore_facts || {};

  return (
    <div className="rpt-page" data-testid="assessment-results">
      <div className="rpt-inner">
        {/* Heading + lead line */}
        <h1 className="rpt-heading" data-testid="report-heading">{report.heading}</h1>
        <p className="rpt-lead">{report.lead_line}</p>

        {isExplore ? (
          <>
            <ExploreAllThree report={report} written={written} exploreFacts={exploreFacts} />
            <PersonalMessage text={report.personal_message} />
          </>
        ) : (
          <RecommendedReport report={report} written={written} pw={pw} />
        )}

        {/* Footer note */}
        <div className="rpt-footer-note" data-testid="report-footer">
          {report.footer?.split('\n\n').map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {/* Nudge line */}
        <p className="rpt-nudge">one last thing, your feedback helps us build a better programme.</p>

        {/* Buttons */}
        <div className="rpt-buttons" data-testid="report-buttons">
          {/* 1. Feedback — primary, full width */}
          {feedbackDone ? (
            <button className="rpt-btn-primary rpt-btn-full disabled" disabled data-testid="feedback-completed-btn">
              Feedback completed
            </button>
          ) : (
            <button className="rpt-btn-primary rpt-btn-full" onClick={() => navigate(`/assessment/feedback/${attemptId}`)} data-testid="answer-feedback-btn">
              <MessageSquare size={18} /> Answer feedback questions
            </button>
          )}

          {/* 2. Email my report */}
          {emailSent ? (
            <button className="rpt-btn-secondary disabled" disabled data-testid="report-sent-btn">
              Report sent
            </button>
          ) : (
            <button className="rpt-btn-secondary" onClick={() => setShowEmailPopup(true)} data-testid="email-report-btn">
              <Mail size={18} /> Email my report
            </button>
          )}

          {/* 3. Learn more about the pathways */}
          <Link
            to="/pathways"
            state={{ fromReport: true }}
            className="rpt-btn-secondary"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            data-testid="learn-pathways-btn"
          >
            Learn more about the pathways
          </Link>
        </div>

        {emailConfirm && <p className="rpt-email-confirm" data-testid="email-confirm">{emailConfirm}</p>}
      </div>

      {/* Email popup */}
      {showEmailPopup && (
        <div className="rpt-popup-overlay" onClick={() => setShowEmailPopup(false)}>
          <div className="rpt-popup" onClick={e => e.stopPropagation()} data-testid="email-popup">
            <h3>Email my report</h3>
            <p>We'll send your report to this email address. You can change it if you'd like it sent somewhere else.</p>
            <input
              type="email"
              value={emailAddr}
              onChange={e => setEmailAddr(e.target.value)}
              data-testid="email-popup-input"
            />
            <div className="rpt-popup-buttons">
              <button className="rpt-btn-primary" onClick={handleSendEmail} disabled={sendingEmail || !emailAddr.includes('@')} data-testid="send-report-btn">
                {sendingEmail ? 'Sending...' : 'Send my report'}
              </button>
              <button className="rpt-btn-outline" onClick={() => setShowEmailPopup(false)} data-testid="cancel-email-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Recommended report layout ──────────────────────────── */
function stripLeadIn(text, leadIn) {
  if (!text || !leadIn) return text || '';
  const t = text.trim();
  const l = leadIn.trim();
  if (t.toLowerCase().startsWith(l.toLowerCase())) {
    return t.slice(l.length).trim();
  }
  return t;
}

function StartHere({ filled, testid }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
      <Link to="/pathways" state={{ fromReport: true }} className={filled ? 'rpt-btn-primary' : 'rpt-btn-outline'}
        style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        data-testid={testid}>Start here</Link>
    </div>
  );
}

function PersonalMessage({ text }) {
  return (
    <div className="rpt-personal" data-testid="personal-message">
      {(text || '').split('\n\n').map((p, i) => <p key={i}>{p}</p>)}
    </div>
  );
}

function RecommendedReport({ report, written }) {
  const r1 = stripLeadIn(written.reason_1, report.reason1_leadin);
  const r2Leadin = written.reason_2_leadin || report.reason2_leadin;
  const r2 = stripLeadIn(written.reason_2, r2Leadin);
  const others = report.other_options?.length ? report.other_options
    : [{ pathway: 'other', name: report.other_option_name, subtitle: report.other_option_subtitle }].filter(o => o.name);
  return (
    <>
      {/* Recommended pathway box */}
      <div className="rpt-card rpt-card-recommended" data-testid="recommended-card">
        <div className="rpt-card-header">
          <h2 className="rpt-card-name">{report.leading_pathway_name}</h2>
          <p className="rpt-card-subtitle">{report.leading_pathway_subtitle}</p>
        </div>
        <div className="rpt-card-body">
          <p><strong>{report.reason1_leadin}</strong> {r1}</p>
          <p><strong>{r2Leadin}</strong> {r2}</p>
          {written.reason_3_bold && (
            <p><strong>{written.reason_3_bold}</strong> {written.reason_3_sentences}</p>
          )}
          {report.closing_line && <p data-testid="recommended-closing-line">{report.closing_line}</p>}
          <StartHere filled testid="start-here-recommended" />
        </div>
      </div>

      <PersonalMessage text={report.personal_message} />

      {/* Your other options */}
      <div className="rpt-other-option" data-testid="other-options">
        <h3>Your other options</h3>
        {others.map((o, i) => (
          <div key={o.pathway + i} className="rpt-card rpt-card-other" data-testid={`other-option-card-${i}`}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <p style={{ margin: 0, fontWeight: 700 }}>{o.name}</p>
              <p style={{ margin: 0 }} className="rpt-card-subtitle-other">{o.subtitle}</p>
            </div>
            <Link to="/pathways" state={{ fromReport: true }} className="rpt-btn-outline"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
              data-testid={`start-here-other-${i}`}>Start here</Link>
          </div>
        ))}
      </div>
    </>
  );
}


/* ── Explore all three layout ───────────────────────────── */
function ExploreAllThree({ report, written, exploreFacts }) {
  const cards = report.cards || [];
  const factMap = {
    founders: exploreFacts?.founders_fact || '',
    employment: exploreFacts?.employment_fact || '',
    independent: exploreFacts?.independent_fact || '',
  };
  return (
    <div className="rpt-explore-cards" data-testid="explore-cards">
      {cards.map((card, i) => (
        <div key={i} className="rpt-card rpt-card-explore" data-testid={`explore-card-${card.pathway}`}>
          <div className="rpt-card-header">
            <h2 className="rpt-card-name">{card.name}</h2>
            <p className="rpt-card-subtitle">{card.subtitle}</p>
          </div>
          <div className="rpt-card-body">
            {factMap[card.pathway] && <p>{factMap[card.pathway]}</p>}
            <p className="rpt-closing">{card.closing}</p>
            <StartHere testid={`start-here-explore-${card.pathway}`} />
          </div>
        </div>
      ))}
    </div>
  );
}
